import { Field, Formik } from 'formik';

import { InformativeReunionStatus, UpdatePersonPayload } from 'services/person/person.types';
import './PersonDetailsForm.scss';

import usePersonDetailsFormVM from './usePersonDetailsFormVM';
import { ContentBlock } from 'components/ContentBlock/ContentBlock';
import FormErrorMessage from 'components/FormErrorMessage/FormErrorMessage';
import { Datalist } from 'components/Datalist/Datalist';
import { AddressAutocomplete } from 'components/Address/Address';
import { Button } from 'components/Button/Button';
import { updateProjectEventSchema } from './PersonDetailsFormSchema';
import plus from 'assets/images/icons/plus.svg';
import { Loader } from 'components/Loader/Loader';
import TipTap from 'components/TipTap/TipTap';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';
import Select from 'react-select';

export function PersonDetailsForm() {
    const { person, handleUpdatePersonInfo, datalists, isLoading, isGetPersonLoading, isGetPersonUninitialized, users } = usePersonDetailsFormVM();

    if (isGetPersonLoading || isGetPersonUninitialized)
        return (
            <div className="person-details-form-loader">
                <Loader />
            </div>
        );
    if (!person) return <>Erreur lors de la récupération des informations de la personne. Veuillez recharger la page</>;

    return (
        <>
            <Formik
                initialValues={person}
                validationSchema={() => updateProjectEventSchema(datalists.datalists || {})}
                onSubmit={async (values: UpdatePersonPayload['body'], { setSubmitting }) => {
                    if(values.informative_reunion?.status !== InformativeReunionStatus.PENDING && values.responsible_person) {
                        delete values.responsible_person;
                    }
                    handleUpdatePersonInfo(values);
                    setSubmitting(false);
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                    <form onSubmit={handleSubmit} className="person-details-form form">
                        <ContentBlock title="Informations principales" isLoading={isLoading}>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Prénom *</label>
                                    <input
                                        type="text"
                                        name="firstname"
                                        placeholder="Doe"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.firstname}
                                        required
                                    />
                                    <FormErrorMessage field="firstname" />
                                </div>
                                <div className="form-group">
                                    <label>Nom *</label>
                                    <input
                                        type="text"
                                        className="uppercase"
                                        name="lastname"
                                        placeholder="Doe"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.lastname}
                                        required
                                    />
                                    <FormErrorMessage field="lastname" />
                                </div>
                            </div>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Adresse mail</label>
                                    <input
                                        type="text"
                                        name="email"
                                        placeholder="johndoe@email.fr"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                    />
                                    <FormErrorMessage field="email" />
                                </div>
                                <div className="form-group">
                                    <label>N° de téléphone *</label>
                                    <input
                                        type="text"
                                        name="phone"
                                        placeholder="Doe"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.phone}
                                        required
                                    />
                                    <FormErrorMessage field="phone" />
                                </div>
                            </div>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Date de naissance</label>
                                    <input
                                        type="date"
                                        name="born_date"
                                        placeholder="johndoe@email.fr"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.born_date}
                                    />
                                    <FormErrorMessage field="born_date" />
                                </div>
                                <div className="form-group">
                                    <label>Département*</label>
                                    <MultiSelect
                                        name="departments"
                                        isMulti={false}
                                        placeholder="Sélectionner le département"
                                        values={
                                            values.departments?.[0]
                                                ? {
                                                      label:
                                                          datalists?.datalists?.departments?.[values.departments[0]] ||
                                                          values.departments[0],
                                                      value: values.departments[0]
                                                  }
                                                : null
                                        }
                                        update={(e: { label: string; value: string } | null) => {
                                            const validDepartments = e && e.value ? [e.value] : [];
                                            setFieldValue('departments', validDepartments);
                                        }}
                                        labelKey="label"
                                        valueKey="value"
                                        options={Object.entries(datalists?.datalists?.departments || {}).map(([code, label]) => ({
                                            label: label,
                                            value: code
                                        }))}
                                        colors={[]}
                                    />
                                    <FormErrorMessage field="departments" />
                                </div>
                                <div className="form-group">
                                    <label>Source</label>
                                    <Datalist
                                        list="person_sources"
                                        name="source"
                                        placeholder="Comment a-t-elle découvert le DAPV?"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.source}
                                    />
                                    <FormErrorMessage field="source" />
                                </div>
                            </div>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Addresse</label>
                                    <AddressAutocomplete baseField="address" formValue={values.address} setValue={setFieldValue} />
                                </div>
                            </div>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Commentaire</label>
                                    <div className="wysiwyg-container">
                                        <TipTap name="observation" value={values.observation} onChange={handleChange} onBlur={handleBlur} />
                                    </div>
                                    <FormErrorMessage field="observation" />
                                </div>
                            </div>
                        </ContentBlock>
                        <ContentBlock title="Rencontre informative" isLoading={isLoading}>
                            <div className="form-line">
                                <div className="form-group">
                                    <label className="checkbox">
                                        <Field type="checkbox" name="informative_reunion.done"/>
                                        Réunion informative réalisée
                                    </label>
                                </div>
                            </div>
                            {values.informative_reunion?.done && (
                                <>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Date de la réunion</label>
                                            <input
                                                type="date"
                                                name="informative_reunion.date"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.informative_reunion?.date || ''}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Intervenant ayant realisé la reunion</label>
                                            <Select
                                                options={users.map((user) => ({
                                                    value: user.id,
                                                    label: user.fullname
                                                }))}
                                                placeholder="Choisir l'intervenant ayant réalisé la réunion"
                                                styles={{
                                                    input: (base) => {
                                                        return {
                                                            ...base,
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        };
                                                    }
                                                }}
                                                defaultValue={
                                                    person.informative_reunion?.supervisor
                                                        ? {
                                                              value: person.informative_reunion?.supervisor?.id,
                                                              label: person.informative_reunion?.supervisor?.fullname
                                                          }
                                                        : {
                                                              value: null,
                                                              label: 'Choisir un intervenant'
                                                          }
                                                }
                                                onChange={(val) => {
                                                    setFieldValue('informative_reunion.supervisor', users.find((user) => user.id === val?.value));
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Lieu de la réunion</label>
                                            <input
                                                type="text"
                                                name="informative_reunion.location"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.informative_reunion.location || ''}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Département</label>
                                            <Datalist
                                                list="departments"
                                                name="informative_reunion.department"
                                                placeholder="Département de la rencontre ?"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.informative_reunion.department || ''}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label htmlFor="">Status de la personne à l'issue de la réunion</label>
                                            <div className="form-line">
                                                <label className="checkbox">
                                                    <Field
                                                        type="radio"
                                                        name="informative_reunion.status"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={InformativeReunionStatus.PENDING}
                                                    />
                                                    Entrée en attente
                                                </label>
                                                <label className="checkbox">
                                                    <Field
                                                        type="radio"
                                                        name="informative_reunion.status"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={InformativeReunionStatus.WAITING}
                                                    />
                                                    Souhaite réfléchir
                                                </label>
                                                <label className="checkbox">
                                                    <Field
                                                        type="radio"
                                                        name="informative_reunion.status"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={InformativeReunionStatus.NO_FOLLOWUP}
                                                    />
                                                    Sans suite
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    {values.informative_reunion.status === InformativeReunionStatus.PENDING && (
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>APPV Attribué</label>
                                                <Select
                                                    options={users.map((user) => ({
                                                        value: user.id,
                                                        label: user.fullname
                                                    }))}
                                                    placeholder="Sélectionnez un APPV"
                                                    styles={{
                                                        input: (base) => {
                                                            return {
                                                                ...base,
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            };
                                                        }
                                                    }}
                                                    defaultValue={
                                                        person.responsible_person
                                                            ? {
                                                                  value: person.responsible_person?.id,
                                                                  label: person.responsible_person?.fullname
                                                              }
                                                            : {
                                                                  value: null,
                                                                  label: 'Choisir un APPV'
                                                              }
                                                    }
                                                    onChange={(val) => {
                                                        setFieldValue('responsible_person', users.find((user) => user.id === val?.value));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </ContentBlock>
                        <ContentBlock title="Caractéristiques" isLoading={isLoading}>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Caractéristique principale</label>
                                    <Datalist
                                        list="disorders"
                                        name="disorders"
                                        placeholder="Sélectionner une caractéristique"
                                        onChange={(value) => {
                                            setFieldValue('disorders.main', value.target.value, false);
                                        }}
                                        onBlur={handleBlur}
                                        value={values.disorders?.main}
                                    />
                                </div>
                            </div>
                            {values.disorders?.secondaries.map((secondary, index) => (
                                <div className="form-line" key={`secondary-${index}`}>
                                    <div className="form-group">
                                        <label>Caractéristique secondaire n°{index + 1}</label>
                                        <Datalist
                                            list="disorders"
                                            name={`disorders.secondaries.${index}`}
                                            placeholder="Sélectionner une caractéristique"
                                            onChange={(value) => {
                                                const newSecondaries = [...(values.disorders?.secondaries || [])];
                                                newSecondaries[index] = value.target.value;
                                                setFieldValue('disorders.secondaries', newSecondaries);
                                            }}
                                            onBlur={handleBlur}
                                            value={secondary}
                                        />
                                        {errors.disorders?.[index] && (
                                            <p className="error">{(errors.disorders as any)[index].secondaries}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div
                                className="button light"
                                style={{ marginBottom: 20 }}
                                onClick={() => {
                                    setFieldValue('disorders.secondaries', [...(values.disorders?.secondaries || []), ''], false);
                                }}
                            >
                                <img src={plus} alt="Ajouter une caractéristique supplémentaire" />
                                Ajouter une caractéristique supplémentaire
                            </div>
                            <div className="form-line">
                                <div className="form-group">
                                    <label>Reconnaissance MDPH</label>
                                    <select
                                        name="disorders.mdph"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.disorders?.mdph}
                                    >
                                        <option value="yes">Oui</option>
                                        <option value="pending">En cours</option>
                                        <option value="no">Non</option>
                                    </select>
                                    {typeof errors.disorders === 'object' && (errors.disorders as any).mdph && (
                                        <p className="error">{(errors.disorders as any).mdph}</p>
                                    )}
                                </div>
                            </div>
                        </ContentBlock>
                        <Button type="submit" label="Valider" />
                    </form>
                )}
            </Formik>
        </>
    );
}
